<template>
  <div class="container" v-if="testData !== null">
    <base-page-title
      title="Scenes"
      subtitle="Analyse the emotional experience per scene"
    ></base-page-title>
    <div class="container" v-if="testData !== null">
      <bg-video-player
        :video="testData.video"
        :getRef="setVideoRef"
        :currentTime="currentTime"
        :timeUpdate="timeUpdate"
        :newScrubberTime="newScrubberTime"
        :updateMetaData="handleVideoLoad"
        :customControls="false"
      />
      <div class="chart-container" v-if="videoRef && duration && testData">
        <bg-emotion-chart-labels
          :data="testData"
          :duration="duration"
          :currentTime="currentTime"
        />
        <bg-emotion-chart
          class="emotion-chart"
          :height="130"
          :data="testData"
          :onGraphClick="setNewTime"
        />
        <bg-scrubber
          :key="scrubberKey"
          :scrubberTimeChange="setNewTime"
          v-if="videoRef"
          :currentTime="currentTime"
          :videoRef="videoRef"
          mouseDownTrigger
        />
        <bg-section
          :data="testData"
          :currentTime="currentTime"
          class="section"
        />
        <bg-legend class="legend" />
      </div>
    </div>
    <div v-if="testData" class="nav">
      <bg-select
        :options="options"
        :value="selectedScene"
        @change="handleSceneSelection"
      ></bg-select>
      <div class="navButtons">
        <div @click="handleNav('backward')">
          <bg-navigation class="navButton" icon="chevronLeft" />
        </div>
        <div @click="handleNav('forward')">
          <bg-navigation class="navButton" icon="chevronRight" />
        </div>
      </div>
    </div>
    <div class="note-container">
      <h2 v-bg-typography="'h2'" :class="['note-container__title']">
        Consultant analysis:
        <span class="note-container__scene-title">{{ scene.label.name }}</span>
      </h2>
      <bg-tab-box initialTab="insights" :tabs="consultantTabNames">
        <template slot="tab-panel-insights"
          ><div v-html="explanationNote"></div
        ></template>
        <template slot="tab-panel-suggestions"
          ><div v-html="suggestionNote"></div
        ></template>
      </bg-tab-box>
    </div>
    <div class="feedback-container" v-if="feedbackSps.length > 0">
      <h2 v-bg-typography="'h2'" :class="['note-container__title']">
        Neurofeedback:
        <span class="note-container__scene-title">{{ scene.label.name }}</span>
      </h2>
      <bg-detail-card
        v-for="feedback in feedbackSps"
        v-bind:key="feedback.id"
        v-bind="{ ...feedback }"
        :text="feedback.comment"
        :detail="feedback.emotion"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
$green: var(--bg-color-green);
$pink: var(--bg-color-pink);
$yellow: var(--bg-color-yellow);
.legend {
  flex-direction: row-reverse;
  padding-bottom: 20px;
}

.chart-container {
  width: 100%;
  height: 160px;
  position: relative;
  margin-top: 15px;
  margin-bottom: 90px;
}

.emotion-chart {
  margin-bottom: 25px;
}

.container {
  max-width: 800px;
  width: 100%;
}

.video-container {
  position: relative;
}

.nav,
.navButtons {
  display: flex;
}

.nav {
  max-width: 70%;
}

.navButton {
  margin-left: 7px;
}

.note-container {
  margin-bottom: 20px;
}

.note-container__scene-title {
  font-weight: 500;
}

.note-container__title {
  padding-top: 20px;
}
</style>

<script>
import moment from "moment";
import BgSectionVideo from "@/components/bg-section-video";
import BgTabBox from "@/components/bg-tab-box";
import BgEmotionChart from "@/components/bg-video-chart/bg-emotion-chart";
import BgSection from "@/components/bg-video-chart/bg-section";
import BgDetailCard from "@/components/bg-detail-card";
import BgVideoPlayer from "@/components/bg-video-chart/bg-video-player";
import BgNavigation from "@/components/bg-navigation";
import BgEmotionChartLabels from "@/components/bg-video-chart/bg-emotion-chart/bg-emotion-chart-labels";
import BgScrubber from "@/components/bg-video-chart/bg-scrubber";
import BgTypography from "@/directives/bg-typography";
import BgSelect from "@/components/bg-select";
import BgLegend from "@/components/bg-legend";
import BasePageTitle from "@/components/base-page-title";
import API from "@/api";
import { mapState } from "vuex";
export default {
  name: "overview",
  components: {
    BgSectionVideo,
    BgEmotionChart,
    BgVideoPlayer,
    BgLegend,
    BgDetailCard,
    BgScrubber,
    BgEmotionChartLabels,
    BgSection,
    BgSelect,
    BgNavigation,
    BasePageTitle,
    BgTabBox,
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      testData: null,
      videoRef: null,
      totalTime: 0,
      currentTime: 0,
      newScrubberTime: 0,
      scrubberSelection: false,
      scrubberKey: 2,
      scene: null,
      selectedScene: {},
      duration: 0,
    };
  },
  computed: {
    ...mapState({
      testState: (state) => state.test,
    }),
    consultantTabNames() {
      return ["insights", "suggestions"];
    },
    consultantTabs() {
      return this.scene.consultancy_notes.filter(
        (n) => n.note_type !== "summary"
      );
    },
    explanationNote() {
      const consultantNote = this.consultantTabs.find(
        (t) => t.note_type === "explanation"
      );
      return consultantNote
        ? consultantNote.note
        : "No further consultant input";
    },
    suggestionNote() {
      const consultantNote = this.consultantTabs.find(
        (t) => t.note_type === "suggestion"
      );
      return consultantNote
        ? consultantNote.note
        : "No further consultant input";
    },
    convertToMMss(newTime) {
      const s = parseInt(newTime % 60);
      const m = parseInt((newTime / 60) % 60);
      const time = moment().seconds(s).minutes(m).format("mm:ss");
      return time;
    },
    feedbackSps() {
      const endTime = this.scene.timestamp_end;
      const startTime = this.scene.timestamp_start;
      const filteredFbs = this.testData.sps
        .filter((sp) => sp.comment !== null)
        .filter((sp) => {
          if (endTime > sp.timestamp_top && sp.timestamp_top > startTime) {
            return sp;
          }
        });
      return filteredFbs;
    },
  },
  watch: {
    "$route.params.id": function (id) {
      this.fetchData();
    },
  },
  methods: {
    handleNav(direction) {
      const currentIndex = this.testData.sections.findIndex(
        (s) => s.id === this.selectedScene.value
      );
      if (direction === "forward") {
        if (currentIndex >= this.testData.sections.length - 1) {
          this.selectedScene = this.options[0];
        } else {
          this.selectedScene = this.options[currentIndex + 1];
        }
      } else {
        if (currentIndex === 0) {
          this.selectedScene = this.options[this.options.length - 1];
        } else {
          this.selectedScene = this.options[currentIndex - 1];
        }
      }

      const activeSection = this.testData.sections.find((s) => {
        return this.selectedScene.label == s.label.name;
      });
      if (activeSection) {
        this.moveScrubber(activeSection.timestamp_start);
      }
    },
    fetchData() {
      const fakeId = this.testState.participants.find(
        (p) => p.type === "video_heatmap"
      ).id;
      const testId = this.$route.params.id;
      API.test.getFakeParticipant(testId, fakeId).then((res) => {
        this.testData = {
          ...res.data,
          significant_points: [],
          sps: res.data.significant_points,
        };
        const scenes = res.data.sections.map((s) => {
          return {
            label: s.label.name,
            value: s.id,
          };
        });

        this.options = scenes;
        const preselectedSceneId = parseInt(this.$route.query.scene_details);

        if (preselectedSceneId) {
          this.selectedScene = this.options.find(
            (s) => s.value === preselectedSceneId
          );
          this.scene = this.testData.sections.find(
            (s) => s.id === preselectedSceneId
          );
        } else {
          this.selectedScene = scenes[0];
          this.scene = this.testData.sections[0];
        }
        if (this.scene) {
          this.moveScrubber(this.scene.timestamp_start);
        }
      });
    },
    handleSceneSelection(value) {
      this.selectedScene = value;
      this.scene = this.testData.sections.find((s) => s.id === value.value);
      if (this.scene && !this.scrubberSelection) {
        this.moveScrubber(this.scene.timestamp_start);
      }
      this.scrubberSelection = false;
    },
    setVideoRef(ref) {
      this.videoRef = ref;
    },
    timeUpdate(time) {
      this.currentTime = time;
      this.checkForSection(time);
    },
    checkForSection(newTime) {
      const currentTime = moment(this.testData.started_at + newTime * 1000);
      const activeSection = this.testData.sections.find((s) => {
        return currentTime.isBetween(s.timestamp_start, s.timestamp_end);
      });
      if (activeSection) {
        this.selectedScene = this.options.find((o) => {
          return activeSection.label.name === o.label;
        });
      }
    },
    setNewTime(newTimePercentage) {
      this.scrubberSelection = true;
      this.newScrubberTime = this.videoRef.duration * newTimePercentage;
    },
    handleVideoLoad(video) {
      this.duration = video.duration;
      if (video) {
        if (this.$route.query.timestamp) {
          const startTime = moment(testState.started_at);
          const endTime = moment(testState.ended_at);
          const startPos = moment(Number(this.$route.query.timestamp));
          const videoLength = endTime.diff(startTime, "seconds");
          const timeElapsed = startPos.diff(startTime, "seconds");
          this.setNewTime(timeElapsed / videoLength);
        }
        this.totalTime = video.duration;
      }
      if (this.scene) {
        this.moveScrubber(this.scene.timestamp_start);
      }
    },
    moveScrubber(time) {
      this.currentTime = 0.1 + (time - this.testData.started_at) / 1000;
      this.newScrubberTime = this.currentTime;
    },
  },
  created() {
    this.fetchData();
  },
};
</script>
